import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getAccessRightProfile} from 'src/openapi/admin/apis/GetAccessRightProfileApi';
import {updateAccessRightProfile} from 'src/openapi/admin/apis/UpdateAccessRightProfileApi';
import {useAppDispatch} from 'src/store/hooks';

import {INIT_PROFILE} from '../../constants';
import {useValidateProfile} from '../../useValidateProfile';

import type {AccessRightsProfile} from 'src/openapi/admin/models/AccessRightsProfile';

type Props = {
    profileId: number,
    notes: string,
};

export const useRoleProfile = ({profileId, notes}: Props) => {
    const apiDispatch = useAppDispatch();
    const {getErrorsByControlId} = useContext(ValidationContext);

    const {
        clearErrors,
        validateProfileName,
        validatePermissions,
        validateDescription,
    } = useValidateProfile();

    const getDispatchAction = useCallback(() => apiDispatch(
        getAccessRightProfile({profileId}),
    ), [profileId, apiDispatch]);

    const saveDispatchAction = useCallback((roleProfile: AccessRightsProfile) => apiDispatch(
        updateAccessRightProfile({profileId, profileUpdateRequest: {
            profileName: roleProfile.profileName,
            profileDescription: roleProfile.profileDescription || '',
            rightsCodes: roleProfile.rightsCodes,
            notes,
        }}),
    ), [profileId, notes, apiDispatch]);

    const saveValidation = useCallback((roleProfile: AccessRightsProfile) => ![
        getErrorsByControlId('profile-name').length,
        validateDescription(roleProfile.profileDescription),
        validatePermissions(roleProfile.rightsCodes),
    ].some(Boolean), [getErrorsByControlId, validateDescription, validatePermissions]);

    const commonDataProps = useCommonDataHook({
        getDispatchAction,
        saveDispatchAction,
        saveValidation,
        initValue: INIT_PROFILE,
    });

    return ({
        ...commonDataProps,
        discardChanges: () => {
            commonDataProps.discardChanges();
            clearErrors();
        },
        validateProfileName,
        validateDescription,
        validatePermissions,
    });
};
